import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import * as Styles from './Highlight.styles';
import useLocale from '@/hooks/useLocale';
import {
  Skeleton,
  Guide,
  Tag,
} from 'gatorcomponents';
import {
  ALL_STUDIO_HIGHLIGHTS_IDS,
  PRODUCTS_HIGHLIGHT_IDS,
  TAB_NAME_TO_ID,
  HIGHLIGHT_ID_TO_ANALYTICS_KEY,
} from '@/pages/Home/HomeHandler.enum.jsx';
import withErrorBoundary from '@/utils/errorBoundary';
import { FloatingToast } from '@/pages/common/FloatingToast';
import VpsN8nHighlight from './VpsN8nHighlight';
import VpsOpenClawHighlight from './VpsOpenClawHighlight';
import EmailTitanHighlight from './EmailTitanHighlight';
import { productsActions } from '@/redux/modules';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { ThemeContext } from 'styled-components';
import HGBuilderHighlight from './HGBuilderHighlight';
import WordPressAIHighlight from './WordPressAIHighlight';
import AiAgentsHighlight from './AiAgentsHighlight';

const Highlight = ({
  elementsHighlight,
  loading,
  size,
  focusOn,
  clearFocusOn,
  mountButtonIsLoading,
  menuIsOpen,
}) => {
  const theme = useContext(ThemeContext);
  const location = useLocation();
  const history = useHistory();
  const defaultGuideCoordinates = {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  };

  const [activeTab, setActiveTab] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [highLightImageLoaded, setHighLightImageLoaded] = useState(false);
  const [guideOn, setGuideOn] = useState(null);
  const [guideCoordinates, setGuideCoordinates] = useState(defaultGuideCoordinates);
  const [showToast, setShowToast] = useState(false);
  const [hgStudioBackground, setHgStudioBackground] = useState(false);

  const summaryState = useSelector(state => state.summary);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(productsActions.products.getProductById(HgProducts.VPS_N8N_ENTRY));
  }, [dispatch]);

  const { home: homeLocale } = useLocale();

  const scrollRef = useRef(null);
  const analyticsFiredRef = useRef({});

  const setGuideOff = useCallback(() => {
    const handleClick = () => {
      if (guideOn === 'studio') {
        setGuideOn(null);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [guideOn]);

  useEffect(() => {
    setGuideOff();
  }, [setGuideOff]);

  const scrollToIndex = useCallback((index, skipTransition = false) => {
    if (!scrollRef.current) {
      return;
    }

    setCurrentIndex(index);

    if (ALL_STUDIO_HIGHLIGHTS_IDS.includes(elementsHighlight[index].id)) {
      setHgStudioBackground(true);
    } else {
      setHgStudioBackground(false);
    }

    const containerWidth = scrollRef.current.offsetWidth;
    if (skipTransition) {
      scrollRef.current.style.transition = 'none';
      scrollRef.current.style.transform = `translateX(-${index * containerWidth}px)`;
      // Force a reflow
      // eslint-disable-next-line no-unused-expressions
      scrollRef.current.offsetHeight;
      scrollRef.current.style.transition = '';
    } else {
      // Ensure transition is enabled
      scrollRef.current.style.transition = '';
      scrollRef.current.style.transform = `translateX(-${index * containerWidth}px)`;
    }
  }, [scrollRef, elementsHighlight]);

  const handleActiveTab = useCallback((newActiveTab, skipTransition = false) => {
    const newCurrentIndex = elementsHighlight.findIndex(element => element.id === newActiveTab);

    setActiveTab(newActiveTab);
    scrollToIndex(newCurrentIndex, skipTransition);
    setGuideOn(null);

    if (ALL_STUDIO_HIGHLIGHTS_IDS.includes(newActiveTab)) {
      setHgStudioBackground(true);
    } else {
      setHgStudioBackground(false);
    }
  }, [elementsHighlight, scrollToIndex]);

  const menuButtonCta = useCallback((newActiveTab) => {
    handleActiveTab(newActiveTab, false);
    setAutoplayEnabled(false);

    const newSearchParams = new URLSearchParams(location.search);
    if (newSearchParams.has('tab')) {
      newSearchParams.delete('tab');
      history.replace({
        pathname: location.pathname,
        search: newSearchParams.toString(),
      });
    }
  }, [handleActiveTab, location, history]);

  const displayStudioGuide = useCallback(() => {
    const timeout = setTimeout(() => {
      setGuideOn('studio');
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  const setFirstTab = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab');

    if (tabFromUrl && elementsHighlight) {
      let tabId;
      if (tabFromUrl === 'hostgator-studio') {
        const studioElement = elementsHighlight.find(element => ALL_STUDIO_HIGHLIGHTS_IDS.includes(element.id));
        tabId = studioElement ? studioElement.id : null;
      } else {
        tabId = TAB_NAME_TO_ID[tabFromUrl] || Number(tabFromUrl);
      }
      const tabExists = elementsHighlight.find(element => element.id === tabId);

      if (tabExists) {
        handleActiveTab(tabExists.id, true);
        setAutoplayEnabled(false);
        if (!analyticsFiredRef.current[tabExists.id] && HIGHLIGHT_ID_TO_ANALYTICS_KEY[tabExists.id]) {
          HIGHLIGHT_ID_TO_ANALYTICS_KEY[tabExists.id]();
          // Avoid firing the same analytics event multiple times
          analyticsFiredRef.current[tabExists.id] = true;
        }
        return;
      }
    }

    const studioChip = elementsHighlight
      && elementsHighlight.length
      && elementsHighlight.find(element => ALL_STUDIO_HIGHLIGHTS_IDS.includes(element.id));

    if (focusOn === 'studio' && studioChip && scrollRef.current) {
      handleActiveTab(studioChip.id, true);
      setAutoplayEnabled(false);
      displayStudioGuide();
      clearFocusOn();
      return;
    }

    if (elementsHighlight && elementsHighlight.length > 0 && activeTab === null) {
      setActiveTab(elementsHighlight[0].id);
    }
  }, [elementsHighlight, activeTab, focusOn, displayStudioGuide, clearFocusOn, handleActiveTab, scrollRef, location.search]);

  const formattedMenuItems = useMemo(() => {
    if (!elementsHighlight || elementsHighlight.length === 0) {
      return [];
    }

    return elementsHighlight.map(element => ({
      icon: element.menu.icon,
      title: element.menu.title,
      id: element.id,
      showFlagNew: element.flag_new,
    }));
  }, [elementsHighlight]);

  const verifyVinculedGoogle = useCallback(() => {
    setShowToast(summaryState.showOAuthUserLink);
  }, [summaryState]);

  useEffect(() => {
    verifyVinculedGoogle();
  }, [verifyVinculedGoogle]);

  useEffect(() => {
    setFirstTab();
  }, [setFirstTab]);

  useEffect(() => {
    const handleResize = () => {
      scrollToIndex(currentIndex);
      setGuideOn(null);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, setGuideOn, scrollToIndex]);

  useEffect(() => {
    if (scrollRef.current) {
      const timeout = setTimeout(() => {
        scrollToIndex(currentIndex, true);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [menuIsOpen, scrollToIndex, currentIndex]);

  useEffect(() => {
    const autoplay = setInterval(() => {
      if (!autoplayEnabled || !elementsHighlight) {
        return;
      }

      const nextIndex = (currentIndex + 1) % elementsHighlight.length;
      scrollToIndex(nextIndex);
      setActiveTab(elementsHighlight[nextIndex].id);
    }, 5000);

    return () => clearInterval(autoplay);
  }, [currentIndex, elementsHighlight, setActiveTab, setCurrentIndex, autoplayEnabled, scrollToIndex]);

  const guideProps = useCallback(() => {
    const popoverContent = (
      <Styles.GuidePopoverContent>
        <Styles.GuidePopoverTitle>{homeLocale.infosMenuHighlight.studioGuide.title}</Styles.GuidePopoverTitle>
        <Styles.GuidePopoverDescription>{homeLocale.infosMenuHighlight.studioGuide.description}</Styles.GuidePopoverDescription>
      </Styles.GuidePopoverContent>
    );

    const maskPadding = 16;

    const steps = [
      {
        popover: {
          content: popoverContent,
          $top: guideCoordinates.top + guideCoordinates.height + (maskPadding * 2),
          $left: guideCoordinates.left - (guideCoordinates.width + maskPadding),
          pointer: 'topRight',
          $maxWidth: 285,
        },
        mask: {
          $width: guideCoordinates.width + (maskPadding * 2),
          $height: guideCoordinates.height + (maskPadding * 2),
          $top: guideCoordinates.top - maskPadding,
          $left: guideCoordinates.left - maskPadding,
        },
      },
    ];

    return {
      steps,
    };
  }, [guideCoordinates, homeLocale]);

  const highLightContentProps = useCallback((element) => {
    const props = {
      key: element.name,
      element,
      activeTab,
      setGuideCoordinates,
      highLightImageLoaded,
      setHighLightImageLoaded,
      buttonIsLoading: mountButtonIsLoading(element.id),
      guideOn,
    };

    return props;
  }, [highLightImageLoaded, activeTab, mountButtonIsLoading, guideOn]);

  if (!activeTab || loading) {
    return (
      <Styles.SkeletonHighlightWrapper $width={size} data-testid="menu-highlight">
        <Styles.MenuHighlight data-testid="menu-highlight-loading">
          <Skeleton height="32px" width="164px" variant="button" />
          <Skeleton height="32px" width="164px" variant="button" />
          <Skeleton height="32px" width="164px" variant="button" />
        </Styles.MenuHighlight>
        <Styles.SkeletonWrapper>
          <Skeleton height="200px" width="350px" variant="rectangle" />
          <Styles.SkeletonWrapper column alignStart>
            <Skeleton height="64px" width="450px" variant="paragraph" />
            <Skeleton height="32px" width="164px" variant="button" />
          </Styles.SkeletonWrapper>
        </Styles.SkeletonWrapper>
      </Styles.SkeletonHighlightWrapper>
    );
  }

  const renderHighlight = (element) => {
    const elements = {
      [PRODUCTS_HIGHLIGHT_IDS.AI_AGENTS]: <AiAgentsHighlight {...highLightContentProps(element)} key={element.id} />,
      [PRODUCTS_HIGHLIGHT_IDS.VPS_N8N]: <VpsN8nHighlight {...highLightContentProps(element)} key={element.id} />,
      [PRODUCTS_HIGHLIGHT_IDS.VPS_OPEN_CLAW]: <VpsOpenClawHighlight {...highLightContentProps(element)} key={element.id} />,
      [PRODUCTS_HIGHLIGHT_IDS.EMAIL_TITAN]: <EmailTitanHighlight {...highLightContentProps(element)} key={element.id} />,
      [PRODUCTS_HIGHLIGHT_IDS.HG_BUILDER]: <HGBuilderHighlight {...highLightContentProps(element)} key={element.id} />,
      [PRODUCTS_HIGHLIGHT_IDS.SITE_CREATOR]: <WordPressAIHighlight {...highLightContentProps(element)} key={element.id} />,
    };

    return elements[element.id];
  };

  return (
    <Styles.BackgroundWrapper $backgroundStudio={hgStudioBackground}>
      {guideOn && highLightImageLoaded && ALL_STUDIO_HIGHLIGHTS_IDS.includes(activeTab) && (
        <Styles.GuideContainer>
          <Guide {...guideProps()} />
        </Styles.GuideContainer>
      )}

      <Styles.HighlightWrapper $width={size} $backgroundStudio={hgStudioBackground}>
        {showToast && (
          <FloatingToast
            title={homeLocale.VinculedGoogle.title}
            message={homeLocale.VinculedGoogle.message}
            context="success"
            duration={3000}
            onClose={() => setShowToast(false)}
          />
        )}

        <Styles.CarouselWrapper>
          <Styles.MenuHighlight data-testid="menu-highlight">
            {formattedMenuItems.map(menuItem => (
              <Styles.MenuHighlightButton
                onClick={() => menuButtonCta(menuItem.id)}
                active={activeTab === menuItem.id}
                key={menuItem.id}
              >
                <Styles.MenuHighlightIcon>
                  {menuItem.icon}
                </Styles.MenuHighlightIcon>

                {menuItem.showFlagNew && (
                  <Styles.TagNewAdapter>
                    <Tag
                      label={homeLocale.infosMenuHighlight.tagNew}
                      variant="gradient"
                      size="sm"
                      gradient={{
                        color: theme.v2.colors.feedback.attentionExtraDark,
                        background: theme.v2.colors.gradient.quaternary,
                      }}
                    />
                  </Styles.TagNewAdapter>
                )}
                <Styles.MenuHighlightTitle dangerouslySetInnerHTML={{ __html: menuItem.title }} />
              </Styles.MenuHighlightButton>
            ))}
          </Styles.MenuHighlight>

          <Styles.CarouselContainer ref={scrollRef}>
            {elementsHighlight.map(element => (
              <Styles.CarouselItem key={element.id}>
                {renderHighlight(element)}
              </Styles.CarouselItem>
            ))}
          </Styles.CarouselContainer>
        </Styles.CarouselWrapper>
      </Styles.HighlightWrapper>
    </Styles.BackgroundWrapper>
  );
};

export default withErrorBoundary(Highlight, 'Highlight');
